/**
 * Shared presentational pieces.
 *
 * Chart components live here too, built in plain HTML/CSS rather than a
 * charting library: at this scale the marks are simple, and hand-built means
 * the spacing, label and color rules are visible in the code instead of
 * buried in a library's defaults.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api.ts';
import { money, type CurrencyInfo } from '../lib/format.ts';

export function Card({
  title,
  actions,
  children,
  flush,
  id,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  /** So a tile elsewhere on the page can scroll to this card. */
  id?: string;
}) {
  return (
    <section className="card" id={id}>
      {(title || actions) && (
        <header className="card__head">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          <div className="spacer" />
          {actions}
        </header>
      )}
      <div className={flush ? 'card__body card__body--flush' : 'card__body'}>{children}</div>
    </section>
  );
}

/**
 * A number, and where to go to see what is behind it.
 *
 * `to` is the whole point of the second version of this component.
 * Every tile in the product summarises a list that exists somewhere —
 * "Accounts not current" is the member roster filtered to not-current,
 * "Over 90 days" is the collections queue — and none of them said so.
 * A figure a manager cannot act on is a figure they read once and stop
 * reading, so the tile IS the link when there is somewhere to go.
 *
 * Whole tile rather than a link on the label: the target is the number,
 * the number is what they are already looking at, and a 12px "view"
 * link in the corner of a tile is a target nobody hits on a phone.
 */
export function Tile({
  label,
  value,
  note,
  tone,
  to,
  onClick,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  tone?: 'good' | 'warning' | 'serious' | 'critical' | 'neutral';
  /** Where the figure comes from. Omit for a tile with nothing behind it. */
  to?: string;
  /**
   * For a figure whose detail is on THIS page — a panel to open, a
   * section to scroll to. `to` navigates; this does not, and a tile
   * should never have both.
   */
  onClick?: () => void;
}) {
  const body = (
    <>
      <div className="tile__label">{label}</div>
      <div className="tile__value">{value}</div>
      {note && (
        <div className="tile__note">
          {tone ? <Status tone={tone}>{note}</Status> : note}
        </div>
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className="tile tile--link">
        {body}
      </Link>
    );
  }
  if (onClick) {
    // A button, not a div with a handler: it has to be reachable by
    // keyboard and announced as pressable, and `all: unset` is what
    // stops the app's button rule painting a navy pill over a figure.
    return (
      <button type="button" className="tile tile--link tile--button" onClick={onClick}>
        {body}
      </button>
    );
  }
  return <div className="tile">{body}</div>;
}

/**
 * A member's name, which goes to that member.
 *
 * Every screen in this product prints member names and until now
 * exactly one of them — the roster — made them clickable. Somebody
 * reading the dashboard, an activity list or a sheet, who wants to know
 * what else is on that account, had to remember the name, open the
 * roster, and search for it.
 *
 * Falls back to plain text when there is no account id rather than
 * rendering a dead link. A guest, an outside player and a walk-up all
 * have names and no account, and a link that goes nowhere is worse than
 * no link — it is a promise the screen does not keep.
 */
export function MemberLink({
  id,
  name,
  className,
}: {
  id: string | null | undefined;
  name: ReactNode;
  className?: string;
}) {
  if (!id) return <span className={className}>{name}</span>;
  return (
    <Link to={`/members/${id}`} className={className}>
      {name}
    </Link>
  );
}

/**
 * Status is always a colored dot PLUS a label. Color alone would fail for a
 * colorblind reader and in forced-colors mode, and these are the states a
 * controller acts on.
 */
export function Status({
  tone,
  children,
}: {
  tone: 'good' | 'warning' | 'serious' | 'critical' | 'neutral';
  children: ReactNode;
}) {
  return (
    <span className={`status status--${tone}`}>
      <span className="status__dot" aria-hidden="true" />
      {children}
    </span>
  );
}

export function AccountStatusPill({ status }: { status: string }) {
  return <span className={`pill pill--${status}`}>{status.replace('_', ' ')}</span>;
}

export function ErrorBanner({ error }: { error: ApiError | null }) {
  if (!error) return null;

  // An invariant violation is not a crash — it is a rule declining
  // something that would have been wrong. Say which rule, so the person can
  // act rather than retry blindly.
  //
  // It said "the ledger" for every invariant, which was true when they
  // were all GL and AR. GOV-7 refusing to delete a committee has nothing
  // to do with the ledger, and telling somebody it does sends them to
  // the wrong screen.
  if (error.isInvariant) {
    const family = (error.invariant ?? '').split('-')[0];
    const who =
      family === 'GOV' ? 'The club’s by-laws'
      : family === 'MEM' ? 'The membership rules'
      : family === 'POL' || family === 'DUES' ? 'The club’s own rules'
      : family === 'GOLF' || family === 'TENNIS' ? 'The booking rules'
      // Deliberately not 'the booking rules'. A refusal here is
      // almost always a waiver, an age or an adult who is not on the
      // squad, and calling that a booking rule sends somebody to the
      // wrong screen looking for a setting.
      : family === 'SHOOT' ? 'The range rules'
      : family === 'BOAT' ? 'The dock rules'
      : family === 'BANK' ? 'The banking rules'
      // The pool's refusals are about water and the people in it — a
      // waiver, a guest with nobody answerable, an uncertified guard
      // on a stand. Telling somebody the LEDGER refused their check-in
      // sends them to the finance screens, which is the exact failure
      // this ladder was written to stop.
      : family === 'POOL' ? 'The pool rules'
      : 'The ledger';
    return (
      <div className="error-banner">
        <strong>{who} refused this ({error.invariant}).</strong>
        <div style={{ marginTop: 4 }}>{error.message}</div>
      </div>
    );
  }

  const fields = error.fieldErrors;
  return (
    <div className="error-banner">
      <strong>{error.message}</strong>
      {fields.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {fields.map((f, i) => (
            <li key={i}>
              <code>{f.path}</code> — {f.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${100 - i * 12}%` }} />
      ))}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Money({
  minor,
  currency,
  signed,
}: {
  minor: number | string | null | undefined;
  currency: CurrencyInfo;
  signed?: boolean;
}) {
  const n = typeof minor === 'string' ? Number(minor) : (minor ?? 0);
  return <span className={signed && n < 0 ? 'neg' : undefined}>{money(minor, currency)}</span>;
}

/* ------------------------------------------------------------------ */
/* Charts                                                              */
/* ------------------------------------------------------------------ */

/**
 * Horizontal bars for ranked magnitude.
 *
 * One series, so no legend — the card title names it. Every bar carries its
 * own value label, which is why there is no value axis: with a dozen rows,
 * direct labels are more readable than making the eye travel to a scale.
 */
export function BarList({
  items,
  currency,
  color = 'var(--series-1)',
}: {
  items: { label: string; value: number; hint?: string }[];
  currency: CurrencyInfo;
  color?: string;
}) {
  const max = Math.max(...items.map((i) => Math.abs(i.value)), 1);
  return (
    <div className="bars">
      {items.map((item) => (
        <div className="bar-row" key={item.label} title={item.hint ?? item.label}>
          <div className="bar-row__label">{item.label}</div>
          <div className="bar-row__track">
            <div
              className="bar-row__fill"
              style={{ width: `${Math.max((Math.abs(item.value) / max) * 100, 1)}%`, background: color }}
            />
          </div>
          <div className="bar-row__value">{money(item.value, currency)}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Aging as a single stacked bar.
 *
 * The four buckets use the fixed status palette rather than a categorical
 * ramp, because current / 30 / 60 / 90+ really are escalating states of
 * concern — good, warning, serious, critical. Every segment is named and
 * valued in the legend below, so the color is reinforcement rather than the
 * only channel.
 */
export function AgingBar({
  buckets,
  currency,
}: {
  buckets: { label: string; value: number; tone: 'good' | 'warning' | 'serious' | 'critical' }[];
  currency: CurrencyInfo;
}) {
  const total = buckets.reduce((a, b) => a + Math.max(b.value, 0), 0);
  const toneVar = {
    good: 'var(--good)',
    warning: 'var(--warning)',
    serious: 'var(--serious)',
    critical: 'var(--critical)',
  } as const;

  return (
    <div>
      {total > 0 ? (
        <div className="aging" role="img" aria-label="Receivables by age">
          {buckets
            .filter((b) => b.value > 0)
            .map((b) => (
              <div
                key={b.label}
                className="aging__seg"
                style={{ width: `${(b.value / total) * 100}%`, background: toneVar[b.tone] }}
                title={`${b.label}: ${money(b.value, currency)}`}
              />
            ))}
        </div>
      ) : (
        <div className="aging" style={{ background: 'var(--surface-sunk)' }} />
      )}

      <div className="aging__legend">
        {buckets.map((b) => (
          <div className="aging__key" key={b.label}>
            <span className="aging__swatch" style={{ background: toneVar[b.tone] }} aria-hidden="true" />
            <span className="muted">{b.label}</span>
            <span className="aging__amount">{money(b.value, currency)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Progress toward a target — used for F&B minimum spend. */
export function Meter({ value, target }: { value: number; target: number }) {
  const pct = target > 0 ? Math.min((value / target) * 100, 100) : 0;
  return (
    <div className="meter" role="img" aria-label={`${Math.round(pct)} percent of minimum met`}>
      <div className="meter__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * A rule the club asked to be shown here.
 *
 * Migration 0102. The club points one of its own by-law provisions at a
 * moment in the product and writes the one line that matters; this shows
 * it, with the citation, at that moment. Nothing about it refuses
 * anything — a notice is what a club uses for the rules the software does
 * not enforce, which at Rockford is thirty-nine of forty-two.
 *
 * Renders nothing at all when a club has asked for nothing here. That is
 * the common case and it must cost the screen no space, no heading and no
 * loading state.
 */
interface Notice {
  id: string;
  headline: string;
  emphasis: string;
  citation: string;
  body: string;
}

export function BylawNotices({ surface }: { surface: string }) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<{ notices: Notice[] }>(`/v1/bylaw-notices?surface=${encodeURIComponent(surface)}`)
      .then((r: { notices: Notice[] }) => {
        if (!cancelled) setNotices(r.notices);
      })
      // A rule that could not be fetched must not break the screen it was
      // meant to help with. It is advice, and advice that fails is silence.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [surface]);

  if (notices.length === 0) return null;

  return (
    <div className="bylaws">
      {notices.map((n) => (
        <div
          key={n.id}
          className={`bylaw${n.emphasis === 'important' ? ' bylaw--important' : ''}`}
        >
          <div className="bylaw__line">
            <span>{n.headline}</span>
            <button
              className="bylaw__cite"
              onClick={() => setOpen(open === n.id ? null : n.id)}
              aria-expanded={open === n.id}
            >
              {n.citation}
            </button>
          </div>
          {/* The full provision, because the headline is the club's
              paraphrase and somebody about to quote it needs the words. */}
          {open === n.id ? <p className="bylaw__body">{n.body}</p> : null}
        </div>
      ))}
    </div>
  );
}
